import { CheckIcon } from 'lucide-react';

import { useEffect, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { CreateContentReportDto } from '@splitfiesta/shared';
import { Card, FormButton, Text } from '@splitfiesta/ui';

import { useTypedApi } from '@/hooks/useTypedApi';

import { toast } from '@/utils/toast';

/**
 * UGC report dialog (store compliance — Apple 1.2 / Play UGC policy).
 * Collects a REQUIRED reason category + REQUIRED typed details, so every
 * report tells the moderators exactly why in the reporter's own words.
 * The composed reason ("harassment — kept insulting me") is sent as the
 * API's single `reason` string; categories are canonical English keys so
 * the moderation inbox is scannable across locales.
 *
 * One instance is mounted app-wide (see App); surfaces open it
 * imperatively via `useReportContent` → `openReportDialog`. Mirrors
 * mobile's ReportSheet.
 */

const REASON_KEYS = ['harassment', 'spam', 'inappropriate', 'other'] as const;
type ReasonKey = (typeof REASON_KEYS)[number];

type Listener = (input: CreateContentReportDto) => void;
let listener: Listener | null = null;

export function openReportDialog(input: CreateContentReportDto) {
  listener?.(input);
}

export function ReportDialogHost() {
  const api = useTypedApi();
  const { t } = useTranslation();
  const commonLoaded = useNamespace('common');

  const [target, setTarget] = useState<CreateContentReportDto | null>(null);
  const [reason, setReason] = useState<ReasonKey | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    listener = input => {
      setTarget(input);
      setReason(null);
      setDetails('');
      setSubmitting(false);
    };
    return () => {
      listener = null;
    };
  }, []);

  if (!target || !commonLoaded) return null;

  const canSubmit = reason !== null && details.trim().length > 0;
  const close = () => setTarget(null);

  const submit = async () => {
    if (!canSubmit || !reason) return;
    setSubmitting(true);
    try {
      await api.moderation.reportContent({
        ...target,
        reason: `${reason} — ${details.trim()}`,
      });
      close();
      toast.success(t('common:report.success'));
    } catch {
      setSubmitting(false);
      toast.error(t('common:report.error'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="report-dialog"
    >
      <Card className="w-full max-w-md">
        <Text className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('common:report.title')}
        </Text>
        <Text
          variant="bodySmall"
          className="mt-1 mb-4 text-gray-500 dark:text-gray-400"
        >
          {t('common:report.confirmBody')}
        </Text>

        <Text
          variant="bodySmall"
          className="mb-2 font-medium text-gray-700 dark:text-gray-300"
        >
          {t('common:report.reasonLabel')}
        </Text>
        <div className="mb-4 space-y-2">
          {REASON_KEYS.map(key => {
            const selected = reason === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setReason(key)}
                disabled={submitting}
                role="radio"
                aria-checked={selected}
                data-testid={`report-reason-${key}`}
                className={`flex w-full items-center justify-between rounded-xl border px-4 py-2.5 text-left text-sm ${
                  selected
                    ? 'border-primary-500 bg-primary-50 text-gray-900 dark:bg-primary-500/10 dark:text-white'
                    : 'border-gray-200 bg-white text-gray-900 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t(`common:report.reasons.${key}`)}
                {selected && <CheckIcon className="h-4 w-4 text-primary-600" />}
              </button>
            );
          })}
        </div>

        <textarea
          value={details}
          onChange={e => setDetails(e.target.value)}
          maxLength={400}
          rows={3}
          disabled={submitting}
          placeholder={t('common:report.detailsPlaceholder')}
          className="mb-4 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-hidden dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          data-testid="report-details-input"
        />

        <FormButton
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          loading={submitting}
          fullWidth
          data-testid="report-submit-button"
        >
          {t('common:report.submit')}
        </FormButton>
        <FormButton
          type="button"
          variant="outline"
          onClick={close}
          disabled={submitting}
          fullWidth
          className="mt-2"
          data-testid="report-cancel-button"
        >
          {t('common:actions.cancel')}
        </FormButton>
      </Card>
    </div>
  );
}
